/**
 * GST Export Utilities
 * Provides CSV generation, WhatsApp message formatting, and deadline calculations
 */

/**
 * Returns upcoming statutory filing dates based on the current/selected date
 */
export function getGstDueDates(referenceDate = new Date()) {
  const ref = new Date(referenceDate);
  const currentMonth = ref.getMonth(); // 0-indexed
  const currentYear = ref.getFullYear();

  // Due dates in the succeeding month
  const nextMonthYear = currentMonth === 11 ? currentYear + 1 : currentYear;
  const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;

  const gstr1DueMonthly = new Date(nextMonthYear, nextMonth, 11);
  const gstr1DueQrmp = new Date(nextMonthYear, nextMonth, 13);
  const gstr3bDueMonthly = new Date(nextMonthYear, nextMonth, 20);
  const gstr3bDueQrmp = new Date(nextMonthYear, nextMonth, 22);

  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  return {
    gstr1Monthly: `11 ${monthNames[nextMonth]} ${nextMonthYear}`,
    gstr1Qrmp: `13 ${monthNames[nextMonth]} ${nextMonthYear}`,
    gstr3bMonthly: `20 ${monthNames[nextMonth]} ${nextMonthYear}`,
    gstr3bQrmp: `22 ${monthNames[nextMonth]} ${nextMonthYear}`
  };
}

/**
 * Escapes CSV text fields
 */
function escapeCsv(val) {
  if (val === null || val === undefined) return '""';
  const str = String(val).replace(/"/g, '""');
  return `"${str}"`;
}

/**
 * Generates and downloads a multi-table CSV report for accountants (Tally / Excel compatible)
 */
export function exportGstReportToCsv(reportData, company = {}, periodLabel = "") {
  if (!reportData) return;

  const { summary, tables } = reportData;
  const rows = [];

  // Header Section
  rows.push(["GST FILING REPORT - COMPREHENSIVE TAX AUDIT"]);
  rows.push(["Company Name", escapeCsv(company.name || "SREE RAM TRADERS")]);
  rows.push(["Company GSTIN", escapeCsv(company.gstin || "33AWFPA8086P1Z3")]);
  rows.push(["Filing Period", escapeCsv(periodLabel || `${reportData.period.fromDate} to ${reportData.period.toDate}`)]);
  rows.push(["Generated On", escapeCsv(new Date().toLocaleString("en-IN"))]);
  rows.push([]);

  // SECTION 1: GSTR-3B Tax Summary
  rows.push(["--- SECTION 1: GSTR-3B TAX COMPUTATION & SUMMARY ---"]);
  rows.push(["Description", "Taxable Value (INR)", "CGST (INR)", "SGST (INR)", "IGST (INR)", "Total Tax (INR)", "Gross Total (INR)"]);
  rows.push([
    "Outward Taxable Sales",
    summary.totalSalesTaxable.toFixed(2),
    summary.salesTax.cgst.toFixed(2),
    summary.salesTax.sgst.toFixed(2),
    summary.salesTax.igst.toFixed(2),
    summary.salesTax.total.toFixed(2),
    summary.totalSalesGross.toFixed(2)
  ]);
  rows.push([
    "Inward Eligible ITC (Purchases)",
    summary.totalPurchasesTaxable.toFixed(2),
    summary.purchasesItc.cgst.toFixed(2),
    summary.purchasesItc.sgst.toFixed(2),
    summary.purchasesItc.igst.toFixed(2),
    summary.purchasesItc.total.toFixed(2),
    summary.totalPurchasesGross.toFixed(2)
  ]);
  rows.push([
    "NET TAX PAYABLE IN CASH",
    "-",
    summary.netPayable.cgst.toFixed(2),
    summary.netPayable.sgst.toFixed(2),
    summary.netPayable.igst.toFixed(2),
    summary.netPayable.total.toFixed(2),
    "-"
  ]);
  rows.push([]);

  // SECTION 2: GSTR-1 Table 4 - B2B Invoices
  rows.push(["--- SECTION 2: GSTR-1 TABLE 4 - B2B INVOICES REGISTER ---"]);
  rows.push(["Invoice No", "Date", "Customer Name", "Customer GSTIN", "POS (State)", "Inter-State", "Taxable Value", "CGST", "SGST", "IGST", "Invoice Total"]);
  if (tables.b2bInvoices.length === 0) {
    rows.push(["No B2B invoices recorded in this period", "", "", "", "", "", "", "", "", "", ""]);
  } else {
    tables.b2bInvoices.forEach((inv) => {
      rows.push([
        escapeCsv(inv.invoiceNo),
        inv.invoiceDate,
        escapeCsv(inv.customerName),
        escapeCsv(inv.customerGstin),
        escapeCsv(inv.placeOfSupply),
        inv.isInterState ? "Yes" : "No",
        inv.taxableValue.toFixed(2),
        inv.cgst.toFixed(2),
        inv.sgst.toFixed(2),
        inv.igst.toFixed(2),
        inv.grandTotal.toFixed(2)
      ]);
    });
  }
  rows.push([]);

  // SECTION 3: GSTR-1 Table 7 - B2C Summary
  rows.push(["--- SECTION 3: GSTR-1 TABLE 7 - B2C & RETAIL SALES SUMMARY ---"]);
  rows.push(["Place of Supply", "Supply Category", "Invoices Count", "Taxable Value", "CGST", "SGST", "IGST", "Total Amount"]);
  if (tables.b2cSummary.length === 0) {
    rows.push(["No B2C sales recorded in this period", "", "", "", "", "", "", ""]);
  } else {
    tables.b2cSummary.forEach((b2c) => {
      rows.push([
        escapeCsv(b2c.pos),
        escapeCsv(b2c.type),
        b2c.count,
        b2c.taxableValue.toFixed(2),
        b2c.cgst.toFixed(2),
        b2c.sgst.toFixed(2),
        b2c.igst.toFixed(2),
        b2c.total.toFixed(2)
      ]);
    });
  }
  rows.push([]);

  // SECTION 4: GSTR-1 Table 12 - HSN / SAC Summary
  rows.push(["--- SECTION 4: GSTR-1 TABLE 12 - HSN / SAC SUMMARY ---"]);
  rows.push(["HSN / SAC Code", "Description", "Unit", "Total Qty", "Rate (%)", "Taxable Value", "CGST", "SGST", "IGST", "Total Value"]);
  if (tables.hsnSummary.length === 0) {
    rows.push(["No HSN items recorded in this period", "", "", "", "", "", "", "", "", ""]);
  } else {
    tables.hsnSummary.forEach((hsn) => {
      rows.push([
        escapeCsv(hsn.hsnCode),
        escapeCsv(hsn.description),
        escapeCsv(hsn.unit),
        hsn.totalQty,
        `${hsn.rate}%`,
        hsn.taxableValue.toFixed(2),
        hsn.cgst.toFixed(2),
        hsn.sgst.toFixed(2),
        hsn.igst.toFixed(2),
        hsn.totalValue.toFixed(2)
      ]);
    });
  }
  rows.push([]);

  // SECTION 5: Inward Purchases (ITC Register)
  rows.push(["--- SECTION 5: INWARD PURCHASES (ITC REGISTER) ---"]);
  rows.push(["Vendor Bill No", "Date", "Vendor Name", "Vendor GSTIN", "Taxable Value", "CGST", "SGST", "IGST", "TCS", "Bill Total"]);
  if (tables.purchaseRegister.length === 0) {
    rows.push(["No purchases recorded in this period", "", "", "", "", "", "", "", "", ""]);
  } else {
    tables.purchaseRegister.forEach((pur) => {
      rows.push([
        escapeCsv(pur.vendorBillNo),
        pur.billDate,
        escapeCsv(pur.vendorName),
        escapeCsv(pur.vendorGstin),
        pur.taxableValue.toFixed(2),
        pur.cgst.toFixed(2),
        pur.sgst.toFixed(2),
        pur.igst.toFixed(2),
        Number(pur.tcs || 0).toFixed(2),
        pur.grandTotal.toFixed(2)
      ]);
    });
  }

  // Convert to CSV string and download
  const csvContent = rows.map((e) => (Array.isArray(e) ? e.join(",") : "")).join("\n");
  const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const cleanLabel = (periodLabel || "Period").replace(/[^a-zA-Z0-9_-]/g, "_");
  link.setAttribute("href", url);
  link.setAttribute("download", `GST_Filing_Report_${cleanLabel}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/**
 * Formats a clean, executive WhatsApp message summarizing GST filing figures for the accountant
 */
export function formatAccountantWhatsAppMessage(reportData, company = {}, periodLabel = "") {
  if (!reportData) return "";
  const { summary } = reportData;
  const companyName = company.name || "SREE RAM TRADERS";
  const gstin = company.gstin || "33AWFPA8086P1Z3";

  return `*GST FILING DATA REPORT* 🏛️
*Business:* ${companyName}
*GSTIN:* ${gstin}
*Period:* ${periodLabel || `${reportData.period.fromDate} to ${reportData.period.toDate}`}

*1. Outward Sales (GSTR-1 & 3B)*
• Gross Turnover: ₹${summary.totalSalesGross.toLocaleString("en-IN")}
• Taxable Value: ₹${summary.totalSalesTaxable.toLocaleString("en-IN")}
• CGST: ₹${summary.salesTax.cgst.toLocaleString("en-IN")}
• SGST: ₹${summary.salesTax.sgst.toLocaleString("en-IN")}
• IGST: ₹${summary.salesTax.igst.toLocaleString("en-IN")}
• Total Output Tax: *₹${summary.salesTax.total.toLocaleString("en-IN")}*
• Invoices: ${summary.counts.totalInvoices} (B2B: ${summary.counts.b2bCount}, B2C: ${summary.counts.b2cCount})

*2. Inward Purchases ITC (GSTR-3B)*
• Total Purchases: ₹${summary.totalPurchasesGross.toLocaleString("en-IN")}
• Taxable Purchases: ₹${summary.totalPurchasesTaxable.toLocaleString("en-IN")}
• Eligible ITC (CGST+SGST+IGST): *₹${summary.purchasesItc.total.toLocaleString("en-IN")}*${summary.totalPurchasesTcs > 0 ? `
• TCS paid to vendors (income-tax credit): ₹${summary.totalPurchasesTcs.toLocaleString("en-IN")}` : ""}

*3. Net Tax Payable (Challan)*
• Net CGST: ₹${summary.netPayable.cgst.toLocaleString("en-IN")}
• Net SGST: ₹${summary.netPayable.sgst.toLocaleString("en-IN")}
• Net IGST: ₹${summary.netPayable.igst.toLocaleString("en-IN")}
👉 *Net Cash Tax Payable: ₹${summary.netPayable.total.toLocaleString("en-IN")}*

_Detailed PDF Report and Excel CSV generated for your audit & filing._`;
}
